<template>
  <VaModal v-model="visible" size="small" close-button hide-default-actions @beforeClose="resetForm">
    <h1 class="va-h5">{{ invoice?.id ? 'Editar ingreso' : 'Registrar nuevo ingreso' }}</h1>
    <VaForm v-slot="{ isValid }" ref="income-form" class="flex-col justify-start items-start gap-4 inline-flex w-full">
      <div class="self-stretch flex-col justify-start items-start gap-4 flex">
        <VaInput
          v-model="formData.products[0].detail"
          placeholder="Describa el detalle de su ingreso."
          :label="$t('ventas.whatBuy')"
          class="w-full required-field"
          :rules="[validators.required]"
        />

        <VaDateInput
          v-model="formData.date"
          :label="$t('ventas.when')"
          class="w-full required-field"
          :rules="[validators.required]"
          name="date"
          :format="formatDate"
        />
        <VaInput
          v-model="formData.subtotalTarifaCero"
          placeholder="Ingrese el valor del ingreso."
          type="number"
          :label="$t('ventas.howMuch')"
          class="w-full required-field"
          :rules="[validators.required]"
          @focus="formData.subtotalTarifaCero ||= null"
          @blur="formData.subtotalTarifaCero ??= 0"
        />
        <VaSelect
          v-model="formData.paymentMethod"
          placeholder="Seleccione método de pago."
          :options="paymentMethods"
          text-by="name"
          class="w-full required-field"
          :label="$t('ventas.paymentMethodUse')"
          :rules="[validators.required]"
        />
        <VaSelect
          v-if="!userStore.isPersonalWorkspace && categories.length"
          v-model="incomeCategory"
          label="Categoría"
          placeholder="Opcional. Elige la categoría del ingreso."
          :options="categoryOptions"
          text-by="label"
          value-by="id"
          clearable
          class="w-full"
        />
        <VaSelect
          v-if="assignmentList.length"
          v-model="assignment"
          label="Presupuesto"
          :options="assignmentList"
          placeholder="Opcional. Asigna a una línea del presupuesto."
          text-by="label"
          value-by="id"
          clearable
          class="w-full"
        />
        <VaSelect
          v-if="fundOptions.length"
          v-model="fundId"
          label="Asignar a un fondo"
          placeholder="Suma a tus ingresos y al disponible."
          :options="fundOptions"
          text-by="detail"
          value-by="id"
          clearable
          class="w-full"
        />
        <VaSelect
          v-if="accounts.length"
          v-model="formData.accountId"
          placeholder="Opcional."
          :options="accountOptions"
          text-by="name"
          value-by="id"
          clearable
          class="w-full"
          label="Cuenta o banco"
        />

        <div class="flex gap-2 flex-col-reverse w-full sm:flex-row justify-end">
          <VaButton
            preset="info"
            color="transparent"
            class="btn-cancel"
            icon="close"
            :loading="isLoading"
            @click="visible = false"
          >
            {{ $t('form.labels.cancel') }}
          </VaButton>
          <VaButton
            color="transparent"
            class="btn-save"
            icon="save"
            :disabled="!isValid"
            :loading="isLoading"
            @click="save"
          >
            {{ $t('form.labels.save') }}
          </VaButton>
        </div>
      </div>
    </VaForm>
  </VaModal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useForm, useModal, useToast } from 'vuestic-ui'
import { useI18n } from 'vue-i18n'
import type { Invoice } from '../../../models/invoice'
import { validators } from '../../../services/utils'
import { useInvoices } from '../../ventas/composables/useInvoices'
import { getAllAccounts } from '../../../services/account'
import { getAllBudgets } from '../../../services/budget'
import { getAllFunds } from '../../../services/fund'
import { getCategories } from '../../../services/invoice'
import type { Account } from '../../../types/account'
import type { Budget } from '../../../types/budget'
import type { Fund } from '../../../types/fund'
import { canReceive } from '../../funds/fundUtils'
import { assignmentFromDetail, assignmentOptions, parseBudgetItemId } from '../assignmentOptions'
import { useUserStore } from '../../../stores/user'
import type { ChatFlowDraft } from '../../../types/chat'

const props = defineProps<{ modelValue: boolean; invoice?: Invoice | null; draft?: ChatFlowDraft | null }>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  saved: []
}>()
const visible = ref(false)
const { t } = useI18n()
const { confirm } = useModal()
const { init: notify } = useToast()
const form = useForm('income-form')
const { isLoading, paymentMethods, ...incomeApi } = useInvoices()
const userStore = useUserStore()
const accounts = ref<Account[]>([])
const budgets = ref<Budget[]>([])
const funds = ref<Fund[]>([])
const fundId = ref<number | null>(null)
const hydrating = ref(false)
const categories = ref<{ id: number; label: string; icon?: string | null }[]>([])
const incomeCategory = ref<number | null>(null)
const assignment = ref<string | null>(null)
const budgetsReady = ref(false)

function selectIcon(icon?: string | null) {
  const name = String(icon || '').trim()
  if (!name) return undefined
  return name.startsWith('mso-') ? name : `mso-${name}`
}

const categoryOptions = computed(() =>
  categories.value.map((item) => ({ id: item.id, label: item.label, icon: selectIcon(item.icon) })),
)
const accountOptions = computed(() =>
  accounts.value.map((item) => ({ id: item.id, name: item.name, icon: selectIcon(item.icon) })),
)

const assignmentList = computed(() =>
  userStore.isPersonalWorkspace ? assignmentOptions(budgets.value, formData.value.date, 'I') : [],
)
const fundOptions = computed(() => funds.value.filter((fund) => canReceive(fund) || fund.id === fundId.value))

watch(assignment, (value) => {
  if (hydrating.value || !value) return
  fundId.value = null
}, { flush: 'sync' })
watch(fundId, (value) => {
  if (hydrating.value || !value) return
  assignment.value = null
}, { flush: 'sync' })

const makeInvoice = () => ({
  id: null,
  number: null,
  client: null,
  products: [{ id: '', name: '', price: 0, code: '', detail: null, category: null }],
  total: 0,
  tax: 0,
  discount: 0,
  code: null,
  paymentMethod: null,
  authorization: [{ status: '', message: '', description: '' }],
  status: '',
  isManual: true,
  accountId: null,
  subtotalTarifaCero: 0,
  subtotal: 0,
  date: new Date(),
  additionalFields: [],
})
const formData = ref<any>(makeInvoice())

watch(
  () => props.modelValue,
  (value) => {
    visible.value = value
    if (value) populateForm()
  },
  { immediate: true },
)
onMounted(async () => {
  const [, accountsResponse, budgetsResponse, categoriesResponse, fundsResponse] = await Promise.all([
    incomeApi.getPaymentMethods(),
    getAllAccounts().catch(() => ({ data: [] })),
    userStore.isPersonalWorkspace ? getAllBudgets().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    userStore.isPersonalWorkspace ? Promise.resolve({ data: [] }) : getCategories('I').catch(() => ({ data: [] })),
    getAllFunds().catch(() => ({ data: [] })),
  ])
  if (Array.isArray(accountsResponse.data)) {
    accounts.value = accountsResponse.data
  }
  if (Array.isArray(budgetsResponse.data)) {
    budgets.value = budgetsResponse.data
  }
  if (Array.isArray(categoriesResponse.data)) {
    categories.value = categoriesResponse.data
  }
  if (Array.isArray(fundsResponse.data)) {
    funds.value = fundsResponse.data
  }
  budgetsReady.value = true
  if (visible.value && props.draft?.movement === 'income') {
    applyDraft()
  }
})

watch(
  () => userStore.activeWorkspace,
  () => {
    if (!userStore.isBusinessWorkspace) return
    getCategories('I').then((response) => {
      if (Array.isArray(response.data)) categories.value = response.data
    }).catch(() => undefined)
  },
)

watch(visible, (value) => {
  emit('update:modelValue', value)
})

watch(accounts, (list) => {
  if (!props.invoice && formData.value.accountId == null && list[0]) {
    formData.value.accountId = list[0].id
  }
})

watch(assignmentList, (list) => {
  if (!budgetsReady.value) return
  if (assignment.value && !list.some((item) => item.id === assignment.value)) {
    assignment.value = null
  }
})

function draftDate(value: string) {
  if (!value) return new Date()
  const date = new Date(`${value}T12:00:00`)
  return Number.isNaN(date.getTime()) ? new Date() : date
}

function draftAccountId(raw: string | undefined) {
  const found = accounts.value.find((item) => String(item.id) === String(raw || ''))
  return found?.id ?? accounts.value[0]?.id ?? null
}

function applyDraft() {
  const draft = props.draft
  if (!draft || draft.kind !== 'payment' || draft.movement !== 'income') return false
  const total = draft.total || 0
  const paymentMethod =
    paymentMethods.value.find((item: { value?: string }) => String(item.value) === draft.paymentMethodId) ?? null
  formData.value = {
    ...makeInvoice(),
    date: draftDate(draft.date),
    subtotalTarifaCero: total,
    total,
    paymentMethod,
    accountId: draftAccountId(draft.accountId),
    products: [
      {
        ...makeInvoice().products[0],
        detail: draft.detail || draft.product || '',
        price: total,
      },
    ],
  }
  assignment.value = draft.budgetItemId || null
  fundId.value = null
  incomeCategory.value = draft.categoryId || null
  return true
}

function populateForm() {
  if (!props.invoice && applyDraft()) return
  if (!props.invoice) {
    formData.value = {
      ...makeInvoice(),
      accountId: accounts.value[0]?.id ?? null,
    }
    assignment.value = null
    fundId.value = null
    incomeCategory.value = null
    return
  }
  const line = (props.invoice as any).productsList?.[0] ??
    props.invoice.products?.[0] ?? { detail: '', category: null, budgetItemId: null }
  formData.value = {
    ...makeInvoice(),
    ...props.invoice,
    date: new Date(props.invoice.date),
    products: [
      {
        id: '',
        name: '',
        price: 0,
        code: '',
        detail: line.detail ?? '',
        category: line.category ?? null,
        budgetItemId: line.budgetItemId ?? null,
      },
    ],
    subtotalTarifaCero: props.invoice.total,
  }
  hydrating.value = true
  fundId.value = Number(line.fundId) || null
  assignment.value = line.fundId ? null : assignmentFromDetail(line)
  hydrating.value = false
  const lineCategory = Number(line.category)
  incomeCategory.value = lineCategory > 0 ? lineCategory : null
}

function resetForm() {
  formData.value = makeInvoice()
  assignment.value = null
  fundId.value = null
  incomeCategory.value = null
}

function formatDate(date: any) {
  return date instanceof Date ? date.toLocaleDateString('es-EC') : new Date().toLocaleDateString('es-EC')
}

async function save() {
  if (!form.validate()) return
  const agreed = await confirm({
    message: t('ventas.generateComprobante', { name: formData.value.client?.fullname }),
    okText: t('form.labels.yes'),
    cancelText: t('form.labels.no'),
    size: 'small',
  })
  if (!agreed) return

  try {
    formData.value.products[0].id = ''
    formData.value.products[0].name = formData.value.products[0].detail
    formData.value.products[0].price = formData.value.subtotalTarifaCero
    formData.value.products[0].cant = 1
    formData.value.total = formData.value.subtotalTarifaCero
    const picked = Number(incomeCategory.value)
    formData.value.products[0].category = userStore.isPersonalWorkspace || !(picked > 0) ? null : picked
    formData.value.products[0].budgetItemId = userStore.isPersonalWorkspace && !fundId.value
      ? parseBudgetItemId(assignment.value)
      : null
    formData.value.products[0].fundId = fundId.value || null
    formData.value.accountId = formData.value.accountId || null
    if (formData.value.id) await incomeApi.update(formData.value)
    else await incomeApi.add(formData.value)
    notify({ message: t('messages.ingresoCreated'), color: 'success' })
    visible.value = false
    emit('saved')
  } catch (error: any) {
    notify({ message: t('messages.invoiceError', { message: error.response?.data ?? '' }), color: 'danger' })
  }
}
</script>
